import { useState } from "react";
import axios from "axios";
import { X, Paperclip, Loader2, Send, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import RecipientInput from "@/components/mail/RecipientInput";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export default function ComposeSheet({ onClose, onSent, replyData }) {
  const [to, setTo] = useState(replyData?.to || []);
  const [cc, setCc] = useState(replyData?.cc || []);
  const [bcc, setBcc] = useState(replyData?.bcc || []);
  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);
  const [subject, setSubject] = useState(replyData?.subject || "");
  const [body, setBody] = useState(replyData?.body || "");
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);

  const handleSend = async () => {
    if (to.length === 0 && cc.length === 0 && bcc.length === 0) {
      toast.error("Please add at least one recipient");
      return;
    }
    if (!subject.trim()) {
      toast.error("Please add a subject");
      return;
    }

    setSending(true);
    try {
      const formData = new FormData();
      const payload = {
        to,
        cc,
        bcc,
        subject: subject.trim(),
        body,
        is_html: false,
        thread_id: replyData?.thread_id || null,
        draft_id: replyData?.draft_id || null,
      };
      formData.append("data", JSON.stringify(payload));
      for (const file of files) {
        formData.append("files", file);
      }

      await axios.post(`${API}/mail/send`, formData, {
        headers: { ...getHeaders(), "Content-Type": "multipart/form-data" },
        withCredentials: true,
      });

      toast.success("Email sent successfully");
      onSent?.();
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || "Failed to send email";
      toast.error(typeof detail === 'string' ? detail.substring(0, 200) : "Failed to send email");
    }
    setSending(false);
  };

  const handleSaveDraft = async () => {
    try {
      const payload = {
        id: replyData?.draft_id || null,
        to,
        cc,
        bcc,
        subject,
        body,
        is_html: false,
        in_reply_to: replyData?.in_reply_to || null,
      };
      await axios.post(`${API}/mail/drafts`, payload, {
        headers: getHeaders(), withCredentials: true,
      });
      toast.success("Draft saved");
    } catch {
      toast.error("Failed to save draft");
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between p-3 border-b border-app bg-surface-item">
        <h3 className="text-sm font-semibold text-app">
          {replyData ? (replyData.isForward ? "Forward" : replyData.isReplyAll ? "Reply All" : "Reply") : "New Message"}
        </h3>
        <button onClick={onClose} className="text-app-secondary hover:text-app">
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Form */}
      <div className="flex-1 overflow-auto">
        <div className="p-3 space-y-3">
          {/* To */}
          <div>
            <label className="text-[10px] font-medium text-app-secondary uppercase block mb-1">To</label>
            <RecipientInput recipients={to} onChange={setTo} placeholder="Add recipients..." />
          </div>

          {/* CC / BCC toggles */}
          <div className="flex gap-2">
            {!showCc && (
              <button onClick={() => setShowCc(true)} className="text-[10px] text-app-secondary hover:text-app underline">
                CC
              </button>
            )}
            {!showBcc && (
              <button onClick={() => setShowBcc(true)} className="text-[10px] text-app-secondary hover:text-app underline">
                BCC
              </button>
            )}
          </div>

          {showCc && (
            <div>
              <label className="text-[10px] font-medium text-app-secondary uppercase block mb-1">CC</label>
              <RecipientInput recipients={cc} onChange={setCc} placeholder="Add CC..." />
            </div>
          )}
          {showBcc && (
            <div>
              <label className="text-[10px] font-medium text-app-secondary uppercase block mb-1">BCC</label>
              <RecipientInput recipients={bcc} onChange={setBcc} placeholder="Add BCC..." />
            </div>
          )}

          {/* Subject */}
          <div>
            <label className="text-[10px] font-medium text-app-secondary uppercase block mb-1">Subject</label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Subject"
              className="w-full text-sm font-medium px-2 py-1.5 border border-app rounded bg-surface-input text-app focus:outline-none focus:border-violet-400 placeholder:text-placeholder"
            />
          </div>

          {/* Body */}
          <div>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Write your message..."
              rows={12}
              className="w-full text-sm font-medium px-2 py-1.5 border border-app rounded bg-surface-input text-app focus:outline-none focus:border-violet-400 resize-none placeholder:text-placeholder"
            />
          </div>

          {/* Attachments */}
          <div>
            <label className="flex items-center gap-1 text-xs text-app-secondary cursor-pointer hover:text-violet-600">
              <Paperclip className="w-3.5 h-3.5" />
              Attach files
              <input type="file" multiple className="hidden" onChange={(e) => setFiles(Array.from(e.target.files || []))} />
            </label>
            {files.length > 0 && (
              <div className="mt-2 space-y-1">
                {Array.from(files).map((f, i) => (
                  <div key={i} className="flex items-center gap-1 text-[10px] text-app-secondary">
                    <FileText className="w-3 h-3" />
                    {f.name}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between p-3 border-t border-app bg-surface-item">
        <Button
          onClick={handleSend}
          disabled={sending}
          className="h-8 text-xs px-4 rounded-none bg-violet-600 hover:bg-violet-500 gap-1.5"
        >
          {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
          Send
        </Button>
        <Button variant="outline" size="sm" onClick={handleSaveDraft} className="h-8 text-xs rounded-none">
          Save Draft
        </Button>
      </div>
    </div>
  );
}
